import type { Dialogues } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { ConversationAction } from '@atlassian/rovo-conversation-actions/types';

import type { useChatStoreActions } from '../chat-store/useChatStoreActions';

import { getActionSeededConversationOptions } from './getActionSeededConversationOptions';

type BoundCreateSeededConversation = ReturnType<
	typeof useChatStoreActions
>['createSeededConversation'];
type CreateSeededConversationOptions = Parameters<BoundCreateSeededConversation>[0];
type CreateSeededConversation = BoundCreateSeededConversation;

interface CreateSeededConversationFromActionInput {
	action: ConversationAction | undefined;
	dialogues: Dialogues[];
	createSeededConversation: CreateSeededConversation;
	fallbackName?: string;
	sourceId?: string;
	id?: string;
	defaultFollowUpSource: CreateSeededConversationOptions['followUpSource'];
	agentId?: string;
}

export const createSeededConversationFromAction = async ({
	action,
	dialogues,
	createSeededConversation,
	fallbackName,
	sourceId,
	id,
	defaultFollowUpSource,
	agentId,
}: CreateSeededConversationFromActionInput): Promise<string | undefined> => {
	const seededConversationOptions = getActionSeededConversationOptions(action);
	const newConversationId = (await createSeededConversation({
		name: seededConversationOptions?.name ?? fallbackName,
		dialogues,
		...(sourceId ? { sourceId } : {}),
		...(id ? { id } : {}),
		followUpSource: (seededConversationOptions?.followUpSource ??
			defaultFollowUpSource) as CreateSeededConversationOptions['followUpSource'],
		...(agentId ? { agentId } : {}),
		...(seededConversationOptions?.tags ? { tags: seededConversationOptions.tags } : {}),
		...(seededConversationOptions?.skipNextFetch
			? { skipNextFetch: seededConversationOptions.skipNextFetch }
			: {}),
	})) as string | undefined;

	if (!newConversationId) {
		return undefined;
	}

	seededConversationOptions?.onCreated?.(newConversationId);
	return newConversationId;
};
